import type { App } from 'vue'
import type { AxiosError } from 'axios'
import apiClient from '@/config/axios'
import { commonErrorLogger } from '@/globals/commonErrorLogger'
import { showErrorPopup } from '@/globals/errorPopupHost'
import { showSuccessPopup } from './successPopupHost'

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please login again.'
const SERVER_UNAVAILABLE_MESSAGE = 'Server is temporarily unavailable. Please try again later.'
const NETWORK_ERROR_MESSAGE = 'No response from the server. Please try again later.'
const INVALID_INPUT_MESSAGE = 'Invalid Input. Please try again.'

const UNHANDLED_REJECTION_MESSAGE = 'Something went very wrong. Please click OK to continue.'
const NOT_FOUND_MESSAGE = 'The requested resource could not be found.'

let isAxiosInterceptorRegistered = false
let isUnhandledRejectionRegistered = false

function getLoginRedirectUrl(): string {
  const baseUrl = import.meta.env.VITE_ENDPOINT_URL?.toString() ?? ''
  return `${baseUrl}/ssologin`
}

function shouldSkipAuthRedirect(): boolean {
  const path = window.location.pathname
  return path === '/' || path === '/HomePage'
}

function serializeAxiosError(error: AxiosError): Record<string, unknown> {
  return {
    status: error.response?.status,
    statusText: error.response?.statusText,
    method: error.config?.method,
    requestUrl: error.config?.url,
    responseData: error.response?.data,
    code: error.code,
    message: error.message
  }
}

function serializeRejectionReason(reason: unknown): {
  message: string
  stack?: string
  details: Record<string, unknown>
} {
  if (reason instanceof Error) {
    return {
      message: reason.message,
      stack: reason.stack,
      details: { name: reason.name }
    }
  }
  return {
    message: String(reason),
    details: { reasonType: typeof reason, reason }
  }
}

async function logAxiosError(error: AxiosError, extra?: Record<string, unknown>): Promise<void> {
  await commonErrorLogger({
    message: error.message,
    stack: error.stack,
    additionalDetails: {
      ...serializeAxiosError(error),
      ...extra
    }
  })
}
async function handleApiError(
  error: AxiosError,
  message: string,
  errorType: string,
  route?: string
) {
  await logAxiosError(error, { errorType })

  showErrorPopup({
    message,
    status: errorType,
    route
  })

  return Promise.reject(error)
}

function registerAxiosErrorInterceptor(): void {
  if (isAxiosInterceptorRegistered) return
  isAxiosInterceptorRegistered = true

  apiClient.interceptors.response.use(
    // handle custom exceptions sent with 200 status
    // success Interceptor
    (response) => {
      if (response.data?.isCustomException === true) {
        showSuccessPopup({
          message: response.data.message ?? 'Operation completed successfully.',
          title: response.data.title ?? 'Success',
          autoClose: true,
          duration: 3000
        })
        console.log('SUCCESS INTERCEPTOR EXECUTED', response)
        return response
      }

      return response
    },
    // Failure Interceptor
    async (error: AxiosError) => {
      if (!error.response) {
        return handleApiError(error, NETWORK_ERROR_MESSAGE, 'network')
      }

      const status = error.response.status

      // Compulsory: 401 Unauthorized (403 treated the same for session expiry)
      if (status === 401 || status === 403) {
        await logAxiosError(error, { errorType: 'auth' })

        if (!shouldSkipAuthRedirect()) {
          showErrorPopup({
            message: SESSION_EXPIRED_MESSAGE,
            route: getLoginRedirectUrl(),
            status: String(status)
          })
        }

        return Promise.reject(error)
      }

      if (status === 500 || status === 503) {
        // const retryCount = config?._retryCount ?? 0

        // if (config && retryCount < MAX_SERVER_RETRIES) {
        //   config._retryCount = retryCount + 1
        //   return apiClient.request(config)
        // }

        return handleApiError(error, SERVER_UNAVAILABLE_MESSAGE, 'server', '/')
      }

      if (status === 422 || status === 400) {
        return handleApiError(error, INVALID_INPUT_MESSAGE, 'validation')
      }

      if (status === 404) {
        return handleApiError(error, NOT_FOUND_MESSAGE, 'not-found')
      }
      await logAxiosError(error, { errorType: 'api' })
      return Promise.reject(error)
    }
  )
}

function registerVueErrorHandler(app: App): void {
  const previousHandler = app.config.errorHandler

  app.config.errorHandler = (err, instance, info) => {
    console.error('VUE ERROR', err, info)

    // const error = err instanceof Error ? err : new Error(String(err))
    // const componentName = getComponentName(instance ?? null)
    // void commonErrorLogger({
    //   message: error.message,
    //   stack: error.stack,
    //   additionalDetails: {
    //     errorType: 'vue-runtime',
    //     componentName,
    //     lifecycleInfo: info
    //   }
    // })

    // showErrorPopup({ message: RUNTIME_ERROR_MESSAGE, status: 'runtime' })

    if (typeof previousHandler === 'function') {
      previousHandler(err, instance, info)
    }
  }
}

function registerUnhandledRejectionHandler(): void {
  if (isUnhandledRejectionRegistered) return
  isUnhandledRejectionRegistered = true

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    console.error('UNHANDLED REJECTION', event.reason)

    const { message, stack, details } = serializeRejectionReason(event.reason)

    void commonErrorLogger({
      message,
      stack,
      additionalDetails: {
        errorType: 'unhandled-rejection',
        ...details
      }
    })

    showErrorPopup({
      message: UNHANDLED_REJECTION_MESSAGE,
      status: 'unhandled-rejection'
    })
  })
}

export const ErrorHandlerPlugin = {
  install(app: App): void {
    registerAxiosErrorInterceptor()
    registerVueErrorHandler(app)
    registerUnhandledRejectionHandler()
  }
}
export default ErrorHandlerPlugin
